// 화면 등록 계층 — **스텝별로 비워 둔 슬롯**
//
// 서버 쪽(src/server/routes.registry.ts)과 같은 이유로 존재한다. 아홉 스텝 중 여럿이
// 자기 화면을 라우터에 물려야 하는데, 그 여럿이 전부 src/web/main.tsx 를 직접 열면
// 여러 갈래가 같은 파일을 편집하게 되고 "파일 교집합 0" 이라는 병렬 안전 근거가
// 거짓이 된다. 그래서 등록을 이 파일 한 겹 뒤로 밀고 **각 스텝은 자기 줄만 채운다.**
//
// **디렉터리 규약으로 자동 수집하지 않는다.** 무엇이 등록됐는지가 어느 파일에도 남지
// 않으면, 두 갈래가 같은 경로를 만들어도 합칠 때까지 아무도 모른다.
//
// 화면 라이브러리를 쓰지 않고 경로 대조를 손으로 한 이유: 1차의 화면이 셋뿐이고
// (진입·열람·작전 구성) 중첩도 파라미터도 없다. 라우터를 들이면 그 라이브러리의
// 규약이 이 슬롯 표와 겹치는 층을 하나 더 만든다.

import type { ReactElement } from 'react'

import { ComposeScreen } from './screens/compose/shell/index.tsx'
import { EntryScreen } from './screens/entry/index.tsx'
import { LedgerScreen } from './screens/ledger/index.tsx'
import { ProbeScreen } from './screens/probe/index.tsx'
import { ViewScreen } from './screens/view/index.tsx'

export interface ScreenSlot {
  /** 주소창 경로. 정확히 일치할 때 고른다. */
  path: string
  /** 사람이 읽는 이름. 문서와 채점 보고가 이 이름으로 화면을 가리킨다. */
  title: string
  render: () => ReactElement
}

/**
 * 등록 슬롯 표. **자기 스텝의 줄만 채운다. 남의 줄을 건드리지 않는다.**
 */
export const screenSlots: ScreenSlot[] = [
  // T1 발사대 — 계측 프로브. 시계 동기와 신호를 사람이 눈과 귀로 확인하고, 렌더된
  //   파형의 성질 셋은 기계가 판정하는 자리다.
  { path: '/probe', title: '계측 프로브', render: () => <ProbeScreen /> },

  // T2 접속 기반 — 진입 화면(S1). 입장 코드 → CT 닉네임 확인 → 게임 ID → 판정.
  { path: '/', title: '진입', render: () => <EntryScreen /> },

  // T3 타이밍 코어 — 계산 원장. 제품 화면이 아니라 **계산에 낸 창**이다. 이 스텝은
  //   순수 함수만 만드는데, 사용자가 코드를 읽지 않으므로 창이 없으면 남는 증거가
  //   "테스트가 초록이다" 라는 자기 진술뿐이다. 정본의 반례와 수비 예시를 고정 시각
  //   위에서 찍어 사람이 문서와 한 자리씩 대조한다.
  { path: '/ledger', title: '계산 원장', render: () => <LedgerScreen /> },

  // T5 작전 구성 attack · T4 roster 명부 · T8 수비 — 작전 구성 화면(S3).
  //   껍데기와 탭 바 마운트는 T2 가 세웠고, **세 탭의 내용은 위 세 스텝이 채운다.**
  //   그 스텝들은 이 줄이 아니라 compose/shell/tabbar.tsx 의 자기 슬롯을 채운다.
  { path: '/compose', title: 'ops 구성', render: () => <ComposeScreen /> },

  // T6 공격 열람 · T8 수비 — 열람 화면(S2). 개인별 deep countdown 과 신호 존.
  //   **연맹원 세션 토큰이 있어야 값이 온다.** 토큰 없이 이 주소를 직접 열면 화면은 서지만
  //   목록이 비는데, 그것이 옳다 — 이 경로가 인증을 지지 않고 서버의 `session` 권한이 진다.
  //
  //   **2026-08-15 결정으로 이 줄의 대상이 껍데기로 바뀌었다.** 작전 타입이 배타적 상태에서
  //   보기 탭 둘이 되면서 `/view` 가 attack 전용이 아니게 됐다 — 껍데기가 room 의 기본 탭을
  //   읽어 둘 중 하나를 먼저 보이고 사람은 탭으로 오간다. **공격 열람 화면 자체는 한 글자도
  //   안 바뀌었고** 껍데기가 그것을 그대로 마운트한다. 되돌릴 때는 이 한 줄만
  //   `ViewAttackScreen` 으로 되돌리고 `screens/view/` 를 지운다.
  { path: '/view', title: 'ops 열람', render: () => <ViewScreen /> },
]

/**
 * 등록된 경로가 아닌 주소로 들어왔을 때 고르는 화면.
 *
 * 진입 화면이 뿌리(`/`)를 잡았으므로 이제 그쪽이다. 계측 프로브는 여전히 `/probe` 에
 * 살아 있고, 그것이 사람이 시계와 소리를 눈과 귀로 확인하는 자리다.
 */
export const FALLBACK_PATH = '/'

export function resolveScreen(pathname: string): ScreenSlot {
  const exact = screenSlots.find((slot) => slot.path === pathname)
  if (exact !== undefined) return exact

  const fallback = screenSlots.find((slot) => slot.path === FALLBACK_PATH)
  if (fallback === undefined) {
    throw new Error(
      `등록된 화면이 없다. src/web/routes.registry.tsx 의 슬롯 표에 최소 ${FALLBACK_PATH} 이 있어야 한다.`,
    )
  }
  return fallback
}
